import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const navigationSections = [
    {
      section: 'Patient Care',
      items: [
        { label: 'Patient Dashboard', path: '/patient-dashboard', icon: 'LayoutDashboard' },
        { label: 'Patient Records', path: '/patient-records', icon: 'FileText' },
        { label: 'Treatment Documentation', path: '/clinical-documentation', icon: 'ClipboardList' },
        { label: 'Treatment Planning', path: '/treatment-planning', icon: 'GitBranch' },
        { label: 'Dental Imaging', path: '/dental-imaging-management', icon: 'Image' },
        { label: 'Diagnostic Results', path: '/lab-results', icon: 'FlaskConical' },
        { label: 'Prescriptions', path: '/medication-management', icon: 'Pill' },
      ]
    },
    {
      section: 'Practice Management',
      items: [
        { label: 'Appointments', path: '/appointment-scheduling', icon: 'Calendar' },
        { label: 'Patient Intake', path: '/dental-patient-intake', icon: 'ClipboardCheck' },
        { label: 'Clinical Workflow', path: '/dental-charting-clinical-workflow', icon: 'Activity' },
        { label: 'Billing', path: '/billing-management', icon: 'DollarSign' },
        { label: 'Analytics', path: '/practice-analytics-dashboard', icon: 'BarChart3' },
        { label: 'Quality Metrics', path: '/quality-metrics-dashboard', icon: 'Award' },
        { label: 'Marketing Hub', path: '/dental-practice-marketing-hub', icon: 'TrendingUp' },
        { label: 'Patient Communication', path: '/patient-communication-hub', icon: 'MessageSquare' },
        { label: 'Provider Credentialing', path: '/provider-credentialing-management', icon: 'ShieldCheck' },
        { label: 'Team Collaboration', path: '/team-collaboration', icon: 'Users' },
      ]
    },
    {
      section: 'Patient Access',
      items: [
        { label: 'Patient Portal', path: '/patient-portal', icon: 'Globe' },
        { label: 'Treatment Plan Review', path: '/treatment-plan-review', icon: 'FileCheck' },
        { label: 'Treatment Outcome Survey', path: '/treatment-outcome-survey', icon: 'ClipboardCheck' },
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => location?.pathname === path;

  const toggleDropdown = (sectionName) => {
    setOpenDropdown(openDropdown === sectionName ? null : sectionName);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-[300] shadow-sm" ref={dropdownRef}>
        <div className="h-full px-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Icon name="Activity" size={28} color="var(--color-primary)" />
            <span className="text-lg font-semibold text-foreground hidden sm:block">Dental Systems</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Dashboard Button */}
            <button
              onClick={() => handleNavigation('/patient-dashboard')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                isActive('/patient-dashboard')
                  ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="LayoutDashboard" size={18} />
              Dashboard
            </button>
            
            {navigationSections?.map((section, sectionIdx) => (
              <div key={sectionIdx} className="relative">
                <button
                  onClick={() => toggleDropdown(section?.section)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    openDropdown === section?.section
                      ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted'
                  }`}
                >
                  {section?.section}
                  <Icon 
                    name={openDropdown === section?.section ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>

                {/* Dropdown Menu */}
                {openDropdown === section?.section && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg py-2 max-h-96 overflow-y-auto z-[310]">
                    {section?.items?.map((item, itemIdx) => (
                      <button
                        key={itemIdx}
                        onClick={() => handleNavigation(item?.path)}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                          isActive(item?.path)
                            ? 'bg-primary/10 text-primary font-medium' :'text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} />
                        <span className="text-sm">{item?.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[290] lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed top-16 left-0 right-0 bottom-0 bg-card z-[300] lg:hidden overflow-y-auto">
          <div className="p-4 space-y-6">
            {navigationSections?.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h3 className="px-2 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {section?.section}
                </h3>
                <div className="space-y-1">
                  {section?.items?.map((item, itemIdx) => (
                    <button
                      key={itemIdx}
                      onClick={() => handleNavigation(item?.path)}
                      className={`w-full px-4 py-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                        isActive(item?.path)
                          ? 'bg-primary/10 text-primary font-medium' :'text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={item?.icon} size={20} />
                      <span className="text-sm">{item?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;